import WebApp from '@twa-dev/sdk'
import { getTelegramUserId, getTelegramUsername } from './telegram'
import {
  AnalyticsEventLayer,
  AnalyticsEventType,
  AnalyticsPage,
  getEventLayer,
  resolveAnalyticsRoute,
} from '../analytics/eventTypes'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''
const SESSION_KEY = 'web3_miniapp_analytics_session_v2'
const LEGACY_SESSION_KEY = 'web3_miniapp_session_id'
const PREVIOUS_PAGE_KEY = 'web3_miniapp_previous_page'
const SESSION_TIMEOUT_MS = 30 * 60 * 1000
const HEARTBEAT_INTERVAL_MS = 30 * 1000

export type AnalyticsData = Record<string, unknown>

interface AnalyticsSessionState {
  sessionId: string
  userId: string
  startedAt: string
  lastActivityAt: string
  startEventSent: boolean
}

export interface AnalyticsPayload {
  event_type: AnalyticsEventType
  event_id?: string
  date?: string
  page?: AnalyticsPage
  path?: string
  route?: string
  referrer_page?: string
  data?: AnalyticsData
  analytics_event_id?: string
  page_view_id?: string
  interaction_id?: string
  event_layer?: AnalyticsEventLayer
}

let activeInteractionId: string | undefined
let interactionTimer: ReturnType<typeof setTimeout> | undefined
let analyticsPageContext: AnalyticsData = {}
let analyticsEnabled = false

export function setAnalyticsEnabled(enabled: boolean) {
  analyticsEnabled = enabled
  if (!enabled) {
    activeInteractionId = undefined
    if (interactionTimer) clearTimeout(interactionTimer)
    interactionTimer = undefined
    analyticsPageContext = {}
  }
}

function createId(prefix: string) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}_${crypto.randomUUID()}`
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

function getCurrentUserId() {
  const rawUserId = getTelegramUserId()
  const numericUserId = Number(rawUserId)
  return {
    raw: String(rawUserId || '999000001'),
    numeric: Number.isFinite(numericUserId) ? numericUserId : 999000001,
  }
}

function readSession(): AnalyticsSessionState | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    return raw ? JSON.parse(raw) as AnalyticsSessionState : null
  } catch {
    return null
  }
}

function writeSession(session: AnalyticsSessionState) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
  sessionStorage.setItem(LEGACY_SESSION_KEY, session.sessionId)
}

function createSession(userId: string, now: Date): AnalyticsSessionState {
  const session = {
    sessionId: createId('sess'),
    userId,
    startedAt: now.toISOString(),
    lastActivityAt: now.toISOString(),
    startEventSent: false,
  }
  writeSession(session)
  return session
}

function getOrCreateSession(): AnalyticsSessionState {
  const now = new Date()
  const user = getCurrentUserId()
  const existing = readSession()
  const lastActivityMs = existing ? Date.parse(existing.lastActivityAt) : 0
  const expired = !lastActivityMs || now.getTime() - lastActivityMs > SESSION_TIMEOUT_MS

  if (!existing || existing.userId !== user.raw || expired) {
    return createSession(user.raw, now)
  }

  existing.lastActivityAt = now.toISOString()
  writeSession(existing)
  return existing
}

export function getAnalyticsSessionId() {
  if (!analyticsEnabled) return ''
  return getOrCreateSession().sessionId
}

export function getPageName(pathname = window.location.pathname) {
  return resolveAnalyticsRoute(pathname).page
}

export function getPreviousPage() {
  return sessionStorage.getItem(PREVIOUS_PAGE_KEY) || undefined
}

export function setPreviousPage(page: string) {
  sessionStorage.setItem(PREVIOUS_PAGE_KEY, page)
}

export function beginAnalyticsInteraction() {
  if (!analyticsEnabled) return undefined
  activeInteractionId = createId('int')
  if (interactionTimer) clearTimeout(interactionTimer)
  interactionTimer = setTimeout(() => {
    activeInteractionId = undefined
  }, 1000)
  return activeInteractionId
}

export function getActiveInteractionId() {
  return activeInteractionId
}

export function setAnalyticsPageContext(context: AnalyticsData) {
  analyticsPageContext = { ...context }
}

export function clearAnalyticsPageContext(keys?: string[]) {
  if (!keys) {
    analyticsPageContext = {}
    return
  }
  for (const key of keys) {
    delete analyticsPageContext[key]
  }
}

export function getAnalyticsPageContext() {
  return { ...analyticsPageContext }
}

export function createPageViewId() {
  return createId('pv')
}

export function getAnalyticsContext() {
  if (!analyticsEnabled) return {}
  const session = getOrCreateSession()
  const resolved = resolveAnalyticsRoute(window.location.pathname)
  return {
    schema_version: 2,
    session_id: session.sessionId,
    session_started_at: session.startedAt,
    interaction_id: activeInteractionId,
    page: resolved.page,
    path: `${window.location.pathname}${window.location.search}`,
    route: resolved.route,
    referrer_page: getPreviousPage(),
    ...getDeviceContext(),
  }
}

function getDeviceContext() {
  return {
    auth_provider: WebApp.initData ? 'telegram' : 'web',
    tg_platform: WebApp.platform || 'unknown',
    tg_color_scheme: WebApp.colorScheme || undefined,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
  }
}

async function sendBody(body: Record<string, unknown>, useBeacon: boolean) {
  if (!analyticsEnabled) return
  const url = `${API_BASE_URL}/api/analytics`
  try {
    await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(WebApp.initData ? { 'X-TG-Init-Data': WebApp.initData } : {}),
      },
      body: JSON.stringify(body),
      keepalive: useBeacon,
    })
  } catch (error) {
    console.error('[analytics] failed to track', body.event_type, error)
  }
}

function buildBody(payload: AnalyticsPayload, session: AnalyticsSessionState) {
  const user = getCurrentUserId()
  const resolved = resolveAnalyticsRoute(window.location.pathname)
  const page = payload.page || resolved.page
  return {
    schema_version: 2,
    analytics_event_id: payload.analytics_event_id || createId('evt'),
    user_id: user.numeric,
    username: getTelegramUsername(),
    session_id: session.sessionId,
    session_started_at: session.startedAt,
    timestamp: new Date().toISOString(),
    page,
    path: payload.path || `${window.location.pathname}${window.location.search}`,
    route: payload.route || resolved.route,
    referrer_page: payload.referrer_page || getPreviousPage(),
    event_type: payload.event_type,
    event_layer: payload.event_layer || getEventLayer(payload.event_type),
    event_id: payload.event_id,
    date: payload.date,
    page_view_id: payload.page_view_id,
    interaction_id: payload.interaction_id || activeInteractionId,
    ...getDeviceContext(),
    data: {
      route_params: resolved.routeParams,
      ...analyticsPageContext,
      ...(payload.data || {}),
    },
  }
}

export async function trackAnalytics(payload: AnalyticsPayload, useBeacon = false) {
  if (!analyticsEnabled) return
  const session = getOrCreateSession()

  if (payload.event_type === 'session_start') {
    if (session.startEventSent) return
    session.startEventSent = true
    writeSession(session)
    payload.analytics_event_id = `session_start:${session.sessionId}`
  } else if (!session.startEventSent) {
    session.startEventSent = true
    writeSession(session)
    await sendBody(buildBody({
      event_type: 'session_start',
      analytics_event_id: `session_start:${session.sessionId}`,
      page: getPageName(),
      data: { inferred: true },
    }, session), useBeacon)
  }

  await sendBody(buildBody(payload, session), useBeacon)
}

export function startAnalyticsHeartbeat() {
  const sendHeartbeat = () => {
    if (!analyticsEnabled || document.visibilityState !== 'visible') return
    void trackAnalytics({
      event_type: 'session_heartbeat',
      data: { visibility_state: document.visibilityState },
    })
  }
  const timer = window.setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS)
  return () => window.clearInterval(timer)
}

export function trackButtonClick(data: AnalyticsData, interactionId?: string) {
  return trackAnalytics({
    event_type: 'button_click',
    interaction_id: interactionId,
    data,
  })
}

export function trackBusinessEvent(
  eventType: AnalyticsEventType,
  data: AnalyticsData = {},
  eventId?: string,
  interactionId?: string,
  useBeacon = false,
) {
  return trackAnalytics({
    event_type: eventType,
    event_id: eventId,
    interaction_id: interactionId,
    data,
  }, useBeacon)
}

export function trackApiEvent(eventType: AnalyticsEventType, data: AnalyticsData) {
  return trackAnalytics({ event_type: eventType, data })
}
